import { useEffect, useState } from "react";
import { useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Field, StatusBadge } from "@fawkes/ui";

import { formatEuro } from "../i18n/format";

// Het factuurblok rechtsboven in de dossierkop (mockup): het factuurnummer, de bedragen, en de
// opvolging — wanneer er herinnerd, gebeld en overgedragen wordt, en of de factuur per e-mail gaat.
// Die vier horen bij het dossier als geheel en blijven zo op elk tabblad zichtbaar.
//
// Er is geen Opslaan-knop: dit blok staat in de kop, niet in een tegel. Elk veld dat je verlaat
// wordt bewaard — een datum die je invult ís de afspraak, en een half ingevulde opvolging die je
// kwijtraakt omdat je doorklikte is erger dan een enkele extra opslag.

export interface FactuurOpvolging {
  herinneringOp: string | null;
  belmomentOp: string | null;
  incassoOp: string | null;
  factuurPerEmail: boolean | null;
}

interface Invoer {
  herinneringOp: string;
  belmomentOp: string;
  incassoOp: string;
  factuurPerEmail: boolean;
}

const vanGegevens = (d: FactuurOpvolging): Invoer => ({
  herinneringOp: d.herinneringOp ?? "",
  belmomentOp: d.belmomentOp ?? "",
  incassoOp: d.incassoOp ?? "",
  // Leeg = per e-mail: dat is hoe een crematorium zijn facturen stuurt, en de mockup zet het vinkje aan.
  factuurPerEmail: d.factuurPerEmail !== false,
});

export function CrematieDossierFactuurblok({
  id,
  opvolging,
  factuurNummer,
  betaalStatus,
  factuurDatum,
  totaal,
  betaald,
  openstaand,
  onOpgeslagen,
}: {
  id: string;
  opvolging: FactuurOpvolging;
  factuurNummer: string | null;
  betaalStatus: string | null;
  factuurDatum: string | null;
  totaal: number | null;
  betaald: number | null;
  openstaand: number | null;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [invoer, setInvoer] = useState<Invoer>(() => vanGegevens(opvolging));
  const [fout, setFout] = useState(false);
  const { mutate } = useCustomMutation();

  const sleutel = JSON.stringify(opvolging);
  useEffect(() => {
    setInvoer(vanGegevens(opvolging));
    setFout(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleutel]);

  const bewaar = (volgende: Invoer) => {
    setFout(false);
    mutate(
      {
        url: `crematorium/dossiers/${id}/factuur-opvolging`,
        method: "put",
        values: {
          herinneringOp: volgende.herinneringOp || null,
          belmomentOp: volgende.belmomentOp || null,
          incassoOp: volgende.incassoOp || null,
          factuurPerEmail: volgende.factuurPerEmail,
        },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onOpgeslagen(), onError: () => setFout(true) },
    );
  };

  const zet = (veld: keyof Invoer, waarde: string | boolean) => {
    const volgende = { ...invoer, [veld]: waarde } as Invoer;
    setInvoer(volgende);
    bewaar(volgende);
  };

  const euro = (n: number | null) => (n == null ? "—" : formatEuro(n, locale));
  const datum = (iso: string | null) => (iso ? iso : "");

  return (
    <aside className="crem-factuurblok">
      <div className="crem-factuurblok__kop">
        <span className="crem-factuurblok__label">{t("crematieDetail.kop.factuur")}</span>
        {betaalStatus && <StatusBadge tone="neutral">{t(`crematies.betaal.${betaalStatus}`)}</StatusBadge>}
      </div>
      <div className="crem-factuurblok__nr">{factuurNummer ?? "—"}</div>

      <div className="crem-detail__totals">
        <div className="crem-detail__info">
          <span className="crem-detail__info-label">{t("crematieDetail.totaal.totaal")}</span>
          <span className="crem-detail__info-value">{euro(totaal)}</span>
        </div>
        <div className="crem-detail__info">
          <span className="crem-detail__info-label">{t("crematieDetail.totaal.betaald")}</span>
          <span className="crem-detail__info-value">{euro(betaald)}</span>
        </div>
        <div className="crem-detail__info">
          <span className="crem-detail__info-label">{t("crematieDetail.totaal.openstaand")}</span>
          <span className="crem-detail__info-value">{euro(openstaand)}</span>
        </div>
      </div>

      <div className="crem-factuurblok__velden">
        {/* De factuurdatum komt van het uitgeven; die lees je hier, je zet hem niet. */}
        <div className="crem-detail__info">
          <span className="crem-detail__info-label">{t("crematieDetail.factuur.datum")}</span>
          <span className="crem-detail__info-value">{factuurDatum ? new Date(factuurDatum + "T00:00:00").toLocaleDateString(locale) : "—"}</span>
        </div>
        <Field
          label={t("crematieDetail.factuur.herinnering")}
          type="date"
          value={datum(invoer.herinneringOp)}
          onChange={(e) => zet("herinneringOp", e.target.value)}
        />
        <Field
          label={t("crematieDetail.factuur.belmoment")}
          type="date"
          value={datum(invoer.belmomentOp)}
          onChange={(e) => zet("belmomentOp", e.target.value)}
        />
        <Field
          label={t("crematieDetail.factuur.incasso")}
          type="date"
          value={datum(invoer.incassoOp)}
          onChange={(e) => zet("incassoOp", e.target.value)}
        />
      </div>

      <label className="crem-check">
        <input
          type="checkbox"
          checked={invoer.factuurPerEmail}
          onChange={(e) => zet("factuurPerEmail", e.target.checked)}
        />
        {" "}{t("crematieDetail.factuur.perEmail")}
      </label>
      {fout && <p className="fawkes-error" role="alert">{t("crematieDetail.factuur.opslaanMislukt")}</p>}
    </aside>
  );
}
